import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import NotificationItem from "./NotificationItem";
import { useNotifications } from "../Contexts/NotificationProvider";
import { playChime, useSoundMuted } from "../Helpers/ShowNotification";
import "../Assets/css/notifications.css";

// Header bell: unread badge and the latest ten. Opening an item marks it read and goes to its page.
const NotificationBell = () => {
    const navigate = useNavigate();
    const { unread, latest, permission, refresh, markRead, markAllRead, enableBrowserAlerts } = useNotifications();
    const [open, setOpen] = useState(false);
    const [soundMuted, setSoundMuted] = useSoundMuted();
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event) => { if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false); };
        const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const toggle = () => {
        if (!open) refresh();
        setOpen((value) => !value);
    };

    const openItem = (item) => {
        setOpen(false);
        markRead(item.notificationId);
        if (item.url) navigate(item.url);
    };

    return (
        <div className="notif-wrap" ref={wrapRef}>
            <button type="button" className="btn app-icon-btn notif-bell" aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} aria-expanded={open} onClick={toggle}>
                <FaBell />
                {unread > 0 ? <span className="notif-badge">{unread > 99 ? "99+" : unread}</span> : null}
            </button>
            {open ? (
                <div className="notif-panel" role="dialog" aria-label="Notifications">
                    <div className="notif-panel-head">
                        <strong>Notifications</strong>
                        <span className="d-flex align-items-center gap-2">
                            <button type="button" className="notif-link notif-sound" aria-pressed={!soundMuted} aria-label={soundMuted ? "Turn notification sound on" : "Mute notification sound"} title={soundMuted ? "Sound is off" : "Sound is on"} onClick={() => { setSoundMuted(!soundMuted); if (soundMuted) setTimeout(playChime, 50); }}>{soundMuted ? <FaVolumeMute /> : <FaVolumeUp />}</button>
                            <button type="button" className="notif-link" onClick={markAllRead} disabled={!unread}>Mark all read</button>
                        </span>
                    </div>
                    {permission === "default" ? (
                        <div className="notif-banner">
                            <span>Get alerts even when this tab is closed.</span>
                            <button type="button" className="btn btn-sm btn-primary" onClick={enableBrowserAlerts}>Turn on</button>
                        </div>
                    ) : null}
                    {permission === "denied" ? <div className="notif-banner is-muted">Browser alerts are blocked. Allow them in your browser's site settings.</div> : null}
                    <div className="notif-list">
                        {latest.length ? latest.map((item) => <NotificationItem key={item.notificationId} item={item} onOpen={openItem} />) : <div className="notif-empty">You are all caught up.</div>}
                    </div>
                    <div className="notif-panel-foot">
                        <button type="button" className="notif-link" onClick={() => { setOpen(false); navigate("/notifications"); }}>View all</button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default NotificationBell;
